import { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.binko.me/api';

// ============================================================
// useFeedbackTrigger
// Single source of truth for post-interaction feedback flow.
// Owns: openFeedback, closeFeedback, primary API calls,
//       nav-state reader on mount.
// Used by: Chats.js (mount point)
// Called by: ChatHeader.js (via prop)
// ============================================================
export default function useFeedbackTrigger({ navigate }) {
  const [feedbackState, setFeedbackState] = useState(null);
  // feedbackState shape:
  // { matchId, otherUserId, triggerSource, skipMet, initialMetInPerson }

  // READ PENDING FEEDBACK ON EVERY RENDER
  // triggerUnmatch/triggerDeleteChat set window.__binko_feedback_pending
  // then navigate to /chats. Chats.js re-renders, this effect fires and picks it up.
  useEffect(() => {
    const p = window.__binko_feedback_pending;
    if (p) {
      window.__binko_feedback_pending = null;
      setFeedbackState({
        matchId:            p.matchId,
        otherUserId:        p.otherUserId,
        triggerSource:      p.triggerSource,
        skipMet:            p.skipMet            ?? false,
        initialMetInPerson: p.initialMetInPerson ?? null,
      });
    }
  });

  const openFeedback = ({ matchId, otherUserId, triggerSource, skipMet = false, initialMetInPerson = null }) => {
    setFeedbackState({ matchId, otherUserId, triggerSource, skipMet, initialMetInPerson });
  };

  const closeFeedback = () => setFeedbackState(null);

  const doUnmatch = async (matchId) => {
    const token = localStorage.getItem('binko_token');
    try {
      await fetch(`${API_BASE}/chat/${matchId}/unmatch`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
    } catch (_) {}
  };

  const doDeleteChat = async (matchId) => {
    const token = localStorage.getItem('binko_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await fetch(`${API_BASE}/chat/${matchId}/unmatch`, { method: 'POST', headers });
    } catch (_) {}
    try {
      await fetch(`${API_BASE}/chat/${matchId}/delete-chat`, { method: 'DELETE', headers });
    } catch (_) {}
  };

  const triggerUnmatch = async (matchId, otherUserId) => {
    await doUnmatch(matchId);
    window.__binko_feedback_pending = {
      matchId, otherUserId, triggerSource: 'unmatch', skipMet: true, initialMetInPerson: false
    };
    navigate('/chats');
  };

  const triggerDeleteChat = async (matchId, otherUserId) => {
    await doDeleteChat(matchId);
    window.__binko_feedback_pending = {
      matchId, otherUserId, triggerSource: 'delete_chat', skipMet: false, initialMetInPerson: null
    };
    navigate('/chats');
  };

  const triggerWeMet = (matchId, otherUserId) => {
    window.__binko_feedback_pending = {
      matchId, otherUserId, triggerSource: 'we_met', skipMet: false, initialMetInPerson: null
    };
    navigate('/chats');
  };

  const triggerReport = (matchId, otherUserId) => {
    window.__binko_feedback_pending = {
      matchId, otherUserId, triggerSource: 'report_button', skipMet: false, initialMetInPerson: null
    };
    navigate('/chats');
  };

  return {
    feedbackState,
    openFeedback,
    closeFeedback,
    triggerUnmatch,
    triggerDeleteChat,
    triggerWeMet,
    triggerReport,
  };
}
